import { createSlice } from "@reduxjs/toolkit";
import { resolveApiMessage } from "../../helpers/apiMessage";

import { getShopInfo, updateShopInfo } from "./thunk";

export interface ShopInfoState {
    shop: any | null;
    loading: boolean;
    error: string | null;
    updating: boolean;
    updateError: string | null;
    updateSuccess: boolean;
}

export const initialState: ShopInfoState = {
    shop: null,
    loading: false,
    error: null,
    updating: false,
    updateError: null,
    updateSuccess: false,
};

const extractPayloadData = (payload: any): any => {
    if (!payload) {
        return undefined;
    }

    if (payload && typeof payload === "object" && "data" in payload) {
        return (payload as Record<string, any>).data;
    }

    return payload;
};

const extractShopPayload = (payload: any): any => {
    if (payload && typeof payload === "object" && "response" in payload) {
        return extractPayloadData((payload as Record<string, any>).response);
    }

    return extractPayloadData(payload);
};

const getShopFromPayload = (payload: any): any => {
    const data = extractShopPayload(payload);

    if (!data) {
        return null;
    }

    if (data && typeof data === "object" && "data" in data && typeof (data as any).data === "object") {
        return (data as any).data;
    }

    return data;
};

const getErrorMessage = (payload: any): string => {
    if (!payload) {
        return resolveApiMessage(payload);
    }

    if (typeof payload === "string") {
        return payload;
    }

    if (payload && typeof payload === "object") {
        const message = (payload as Record<string, any>).message;
        if (typeof message === "string") {
            return message;
        }

        const errors = (payload as Record<string, any>).errors;
        if (typeof errors === "string") {
            return errors;
        }
    }

    return resolveApiMessage(payload);
};

const ShopInfoSlice = createSlice({
    name: "ShopInfo",
    initialState,
    reducers: {
        clearShopInfoState(state) {
            state.updateSuccess = false;
            state.updateError = null;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getShopInfo.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(getShopInfo.fulfilled, (state, action) => {
            state.loading = false;
            state.shop = getShopFromPayload(action.payload);
        });
        builder.addCase(getShopInfo.rejected, (state, action) => {
            state.loading = false;
            state.error = getErrorMessage(action.payload);
        });
        builder.addCase(updateShopInfo.pending, (state) => {
            state.updating = true;
            state.updateError = null;
            state.updateSuccess = false;
        });
        builder.addCase(updateShopInfo.fulfilled, (state, action) => {
            state.updating = false;
            state.updateSuccess = true;
            const updatedShop = getShopFromPayload(action.payload);
            if (updatedShop) {
                state.shop = updatedShop;
            }
        });
        builder.addCase(updateShopInfo.rejected, (state, action) => {
            state.updating = false;
            state.updateError = getErrorMessage(action.payload);
        });
    },
});

export const { clearShopInfoState } = ShopInfoSlice.actions;
export default ShopInfoSlice.reducer;
